import {
  buildOfferPresentation, dealStrikethrough, offerScopeNote,
} from '@platform/shared-types';
import type {
  OfferInputs, OfferCatalog, PricePhase, PhasedLine,
} from '@platform/shared-types';
import {
  dollarsToCents, deviceTermError, toDeviceTerm, offerTermError, toOfferTerm,
  type BillingSkuRow, type DeviceTermInput, type OfferTermInput,
} from '../services/billingApi';
import {
  FORM_PARSERS,
  type DealFormValues, type PackageRecordLike,
} from '../services/dealPricing';
import type { DeviceBundleRow, DeviceModelRow } from '../services/hardwareApi';

/**
 * THE DEAL BUILDER'S PREVIEW MODEL — now an ADAPTER, not an implementation.
 *
 * One PURE function — no hooks, no fetches, nothing async — that turns the
 * form currently on screen into exactly what a customer would see: the
 * landing page's pricing card, and the signup screen's deal banner
 * (`DealLandingPreview.tsx` renders both from this). Kept out of that
 * component, and out of `DealsPage.tsx`, for the same reason `dealPricing.ts`
 * is: this repo can run plain vitest but has no DOM renderer, so a decision
 * left inside a component is untestable.
 *
 * IT INVENTS NOTHING AT ALL ANY MORE. Every constant, formatter and figure it
 * used to hold by hand now comes from `buildOfferPresentation` in
 * `@platform/shared-types`, which is a verbatim port of what stood here. This
 * file's whole remaining job is the SEAM between the editor and that model:
 *
 *   - the form's price is a typed dollar STRING and the model takes CENTS
 *     (`offerInputsFromForm`);
 *   - the form's discounts, intros and terms are typed STRINGS, so the
 *     editor's own parsers have to be handed in (`parseFormTerm` and
 *     `FORM_PARSERS`), or a half-typed entry would price something the
 *     operator cannot save — and a legacy entry would price nothing at all;
 *   - the model's field names are mapped back onto `DealPreviewModel`'s,
 *     which are NOT renamed: nothing reading this file changes.
 *
 * A characterisation snapshot (`dealPreviewModel.test.ts`) was recorded
 * against the hand-written version and is what proves the port moved no
 * number and no word. It is not a snapshot to update.
 */

export interface DealPreviewFeature {
  id: string;
  /** The catalog's name, or the raw id when the package is not in the catalog. */
  name: string;
  /**
   * The package's own mark and colour, shown beside the tick exactly as the
   * live card shows it. Absent for the static fallback bullets and for an id
   * with no catalog row — both render a plain tick.
   */
  icon?: string;
  color?: string;
  colorType?: string;
  gradientFrom?: string;
  gradientTo?: string;
}

/**
 * ONE WAY TO PAY for one device, as the customer-facing preview shows it.
 *
 * The landing card lets a visitor CHOOSE between finance / rent / buy and shows
 * one price at a time (`DeviceRow` in platform-app's `DealPricingCards`); this
 * preview mirrors that, so an operator sees the card a customer will see rather
 * than a flattened list of every option at once.
 *
 * Prices are DEAL prices — `deviceDealTiers` at one unit, the same function
 * billing-service charges through — with the catalog's own price alongside for
 * the strikethrough.
 */
export interface DealPreviewOption {
  skuId: string;
  /** What the chooser button says: "Finance", "Rent", "Buy". */
  tab: string;
  /** The full priced sentence for the selected option. */
  priceLabel: string;
  saleMode?: string;
  /**
   * Per unit, deal-priced, with the catalog's own beside it for a "was" price.
   *
   * These four names match `SelectedDeviceOption` in shared-types EXACTLY, so
   * an option is passed to `dealCardTotals` verbatim. A mapping layer here is
   * precisely where the preview would drift from the live card it mirrors.
   */
  amountCents: number;
  listAmountCents: number;
  /** Up front on a financed option. 0 means "$0 down", which is worth saying. */
  downPaymentCents: number;
  listDownPaymentCents: number;
  discounted: boolean;
  /**
   * The LIMITED-TIME price for this option and how long it runs, so the card can
   * lead with what the customer actually pays first. Equal to `amountCents` with
   * `promoMonths` absent when there is no promotion.
   */
  promoAmountCents: number;
  promoMonths?: number;
  /**
   * HOW FAR THIS OPTION'S PRICE REACHES — "first 2 at each location", "at your
   * first 3 locations" — or null when it reaches every unit at every location.
   *
   * `covers`/`coveredUnits` and `coveredLocations` were both settable and both
   * priced from (`deviceDealTiers` tiers on the first, `termAppliesAtLocation`
   * gates on the second), but NEITHER reached the customer-facing card: it
   * previews one unit at one location, so a price limited to the first two
   * terminals at the first two stores rendered identically to one with no limit
   * at all. A price whose conditions are invisible is the condition a customer
   * disputes on their second invoice.
   */
  scopeNote: string | null;
}

export interface DealPreviewDevice {
  /** Stable identity (bundleId / modelId / name / skuId) — what selection state is keyed by. */
  key: string;
  name: string;
  /** Bundle component list ("Terminal, Cash Drawer x2"), or a description when there is nothing to list. */
  contentsSummary: string;
  heroImageUrl: string | null;
  priceLabel: string;
  /** True the moment there is no photo to show — drives the amber "no hero image" warning. */
  missingImage: boolean;
  /**
   * Every way to pay for THIS device — "$990.00 outright", "$45.00/mo × 24",
   * "$15.00/mo rental".
   *
   * One device offered to rent, finance and buy is three SKUs on the deal but
   * ONE thing a customer chooses, and listing it three times read as three
   * different terminals. `priceLabel` stays as the first option so nothing
   * that already reads it changes.
   */
  priceLabels: string[];
  /**
   * Every way to pay, as CHOOSABLE options. `priceLabels` is kept beside it
   * (a field is never removed) and still carries the same strings, so anything
   * reading it is untouched.
   */
  options: DealPreviewOption[];
}

export interface DealPreviewModel {
  label: string | null;
  labelIsTrialFallback: boolean;
  name: string;
  priceCents: number | null;
  strikethrough: { show: boolean; wasCents: number; percentOff: number };
  blurb: string;
  features: DealPreviewFeature[];
  /**
   * TRUE when `features` is the STATIC fallback list, not real package names —
   * mirrors the live card's own fallback (`DEFAULT_FEATURES` in platform-app's
   * `rubytill-landing/pricing.ts`) so a deal with no packages checked previews
   * exactly what a customer would actually see, not an empty list.
   */
  featuresAreFallback: boolean;
  devices: DealPreviewDevice[];
  /**
   * THE SOFTWARE PRICE OVER TIME, from the same `buildPricePhases` the
   * subscription is scheduled on.
   *
   * A limited-time price a customer is not told expires is the most
   * complained-about pattern in subscription billing, and disclosing it is not
   * optional in several jurisdictions — so the card gets the schedule, not just
   * the settled figure it used to show. `softwareChanges` is false and the
   * phases are a single flat entry when nothing is promoted, which is what makes
   * this safe to read unconditionally.
   */
  softwarePhases: PricePhase[];
  /**
   * The software side as PHASED LINES, one per package.
   *
   * The card interleaves these with whichever hardware option is selected, so a
   * schedule where the software and the terminal promote for DIFFERENT lengths
   * produces all of its steps. Collapsing the software to a single first/settled
   * pair first would drop the middle phase — which is exactly the "lie by
   * omission" `phaseDisclosure` documents: the year at the highest price is the
   * one the customer least expects and is the one that goes missing.
   */
  softwareLines: PhasedLine[];
  /** What the software costs in month one. Equal to `priceCents` when nothing is promoted. */
  softwareFirstCents: number;
  /** What it settles at once every promotion is over. */
  softwareSettlesAtCents: number;
  softwareChanges: boolean;
  trialDays: number;
  code: string;
  /**
   * Straight passthrough of the form's own `isDefaultDeal` — the preview's
   * ruby-glow "featured" card styling is conditional on this (Task 3e): the
   * live landing page only draws it for the default deal, and a preview that
   * always drew it would show every deal as the one it isn't.
   */
  isDefaultDeal: boolean;
}

/*
  DELETED, DELIBERATELY: `DEFAULT_BLURB`, `DEFAULT_FEATURES`, `formatDollars`,
  `priceLabelFor` and `tabFor` all lived here as hand-copies of platform-app's
  `rubytill-landing/pricing.ts` — two repos with no compiler between them, so
  every edit to one was a silent divergence. They are now
  `DEFAULT_OFFER_BLURB`, `DEFAULT_OFFER_FEATURES`, `formatOfferDollars`,
  `offerPriceLabel` and `offerTabLabel` in `@platform/shared-types`, reached
  through `buildOfferPresentation`. Do not restore a local copy of any of them.
*/

/**
 * `offerScopeNote` is the shared one, re-exported UNCHANGED so every caller and
 * every test that imports it from here is untouched (the additive rule: this
 * file keeps every export it has today).
 */
export { offerScopeNote };

/**
 * THE EDITOR'S FORM AS MODEL INPUTS.
 *
 * The one field that genuinely differs between the editor and the server: the
 * form's price is a typed dollar STRING, and `dollarsToCents` is what refuses a
 * half-typed one rather than letting it become a number mid-keystroke.
 */
export function offerInputsFromForm(form: DealFormValues): OfferInputs {
  return {
    code: form.code,
    name: form.name,
    priceCents: dollarsToCents(form.priceDollars).cents ?? null,
    packageIds: Array.from(new Set(form.packageIds || [])),
    deviceSkuIds: Array.from(new Set(form.deviceSkuIds || [])),
    offerTerms: form.offerTerms || {},
    packageDiscounts: form.packageDiscounts || {},
    packageIntros: form.packageIntros || {},
    deviceTerms: form.deviceTerms || {},
    landingPageLabel: form.landingPageLabel || '',
    landingPageDescription: form.landingPageDescription || '',
    isDefaultDeal: !!form.isDefaultDeal,
    trialDays: typeof form.trialDays === 'number' && Number.isFinite(form.trialDays) ? form.trialDays : 0,
  };
}

/**
 * An `OfferTermInput` carries controls a `DeviceTermInput` has never had.
 *
 * 🔴 THIS DISCRIMINATOR IS STRUCTURAL, NOT NOMINAL — it holds only because
 * `DealFormValues.offerTerms` is typed `Record<string, OfferTermInput>` and all
 * five of these are REQUIRED on that interface, so every entry the editor puts
 * in that map carries them (`BLANK_OFFER_TERM` and `offerTermToInput` both set
 * all five, `promoEnabled: false` and `coveredLocations: ''` included — the
 * check is `in`, not truthiness, so a blank value still identifies an offer).
 *
 * Relax that typing — make any of the five optional, or hand-build an
 * `offerTerms` entry from a partial literal — and a device offer missing them
 * is routed to `toDeviceTerm`, which has no slot for a promotion or for
 * `coveredLocations` and drops both WITHOUT ERROR. The card then quotes a
 * settled price where a limited-time one was promised, and an unlimited reach
 * where the offer covered the first N locations. Do not narrow this list, and
 * do not make any of these fields optional on `OfferTermInput`.
 */
const OFFER_ONLY_KEYS = ['promoEnabled', 'promoMode', 'promoValue', 'promoMonths', 'coveredLocations'] as const;

/**
 * THE EDITOR'S TERM PARSER.
 *
 * A half-typed discount is REJECTED so the card holds at LIST rather than
 * flickering to a number the operator cannot save. (The server passes the
 * identity function instead — its terms are already parsed.)
 *
 * 🔴 IT MUST DISPATCH ON SHAPE. One `parseTerm` serves BOTH `offerTerms` and
 * the legacy `deviceTerms`, and the two carry different input shapes.
 * `deviceShape()` sets `hasQuantity: true` and `PACKAGE_SHAPE` sets it false,
 * so that flag says whether a DEVICE term is possible at all.
 *
 * 🔴 AND ON THE TERM ITSELF, because `hasQuantity` alone is not enough: on a
 * device line the model asks this function about BOTH maps. A `DeviceTermInput`
 * handed to `toOfferTerm` — or, just as bad, an `OfferTermInput` handed to
 * `toDeviceTerm`, which knows nothing of `promo` and would drop a limited-time
 * price on the floor — silently prices the device wrong. So a device term is
 * recognised by the absence of every offer-only control, which is exactly what
 * `deviceTermToInput` produces and `offerTermToInput` never does.
 */
const parseFormTerm: OfferCatalog['parseTerm'] = (term, shape) => {
  if (!term || typeof term !== 'object') return undefined;
  const isLegacyDeviceTerm = shape.hasQuantity && !OFFER_ONLY_KEYS.some((k) => k in term);
  if (isLegacyDeviceTerm) {
    const input = term as DeviceTermInput;
    return deviceTermError(input) ? undefined : (toDeviceTerm(input) as any);
  }
  const input = term as OfferTermInput;
  return offerTermError(input, shape) ? undefined : toOfferTerm(input, shape);
};

/*
  DELETED, DELIBERATELY: `optionFor` and `resolveDevice`. Both are now private
  to `buildOfferPresentation`, character for character. The device grouping
  (`bundleId` / `modelId` / name / `skuId`), the `deviceDealTiers` pricing at
  one unit and the `offerAmounts` promotion all moved with them.
*/

/**
 * THE ADAPTER. One call to the shared model, then a straight rename onto the
 * `DealPreviewModel` field names — which are NOT changed, so nothing reading
 * this file is touched.
 *
 * The two seams it holds are the only reason this function still exists:
 * `parseFormTerm` (the editor's typed terms) and `FORM_PARSERS` (the editor's
 * typed LEGACY discounts and intros). Drop either and a deal that carries only
 * the old fields previews at LIST while the editor beside it shows a discount.
 */
export function buildDealPreviewModel(
  form: DealFormValues,
  packages: PackageRecordLike[],
  deviceSkus: BillingSkuRow[],
  bundles: DeviceBundleRow[],
  models: DeviceModelRow[],
): DealPreviewModel {
  const inputs = offerInputsFromForm(form);
  const presentation = buildOfferPresentation(inputs, {
    packages,
    deviceSkus,
    bundles,
    models,
    parseTerm: parseFormTerm,
    // 🔴 REQUIRED. The shared defaults read ALREADY-PARSED stored shapes; the
    // editor's `packageDiscounts` / `packageIntros` are typed strings and are
    // rejected outright without these. See `FORM_PARSERS` in `dealPricing.ts`.
    parsers: FORM_PARSERS,
  });

  const priceCents = inputs.priceCents;
  const phases = presentation.phases;

  return {
    label: presentation.label,
    /*
      Always false. A "{n}-day trial" fallback label used to be derived here;
      trials retired 2026-08-28, so there is no fallback left to be in. The
      field is kept — never removed — so nothing reading it breaks.
    */
    labelIsTrialFallback: false,
    name: presentation.name,
    priceCents,
    // `dealStrikethrough` on the SAME packages sum the model itself used —
    // `software.listCents` IS that sum, already gated for an unpriced package.
    // The one pure function, on the one pair of numbers: not a second opinion.
    strikethrough: dealStrikethrough(presentation.software.listCents, priceCents),
    blurb: presentation.blurb,
    /*
      SHAPE-PRESERVING on purpose. A real selection has always carried all five
      identity keys (as `undefined` when the package sets none); the static
      fallback has always carried only `{ id, name }`. The shared model omits
      absent keys instead, which reads better but is a different object — and
      "the object is different" is precisely what this repo's characterisation
      snapshot exists to refuse. Restored here, at the seam, rather than by
      making the shared model emit keys it does not need.

      ONE ACCEPTED DELTA, recorded rather than fixed: a package whose `icon` (or
      colour) is the EMPTY STRING used to arrive here as `''` and now arrives as
      `undefined`, because the shared model gates those keys on truthiness. It
      is invisible in every direction — no renderer distinguishes `''` from
      `undefined` for an icon name, `toEqual` treats the two objects as equal,
      and both mean "this package has no mark". Preserving `''` would mean
      re-reading the packages catalog by id here purely to carry a blank string,
      which is a second lookup and a second place to drift. If a future surface
      ever gives `''` a meaning distinct from "unset", this is the line to fix.
    */
    features: presentation.featuresAreFallback
      ? presentation.features.map((f) => ({ id: f.id, name: f.name }))
      : presentation.features.map((f) => ({
        id: f.id,
        name: f.name,
        icon: f.icon,
        color: f.color,
        colorType: f.colorType,
        gradientFrom: f.gradientFrom,
        gradientTo: f.gradientTo,
      })),
    featuresAreFallback: presentation.featuresAreFallback,
    devices: presentation.devices,
    softwarePhases: phases,
    softwareLines: presentation.software.lines,
    // With no priced packages the schedule is empty and its figures are 0; the
    // deal's own typed price is what the card then shows, as it always has.
    softwareFirstCents: phases.length ? presentation.software.firstCents : (priceCents ?? 0),
    softwareSettlesAtCents: phases.length ? presentation.software.settlesAtCents : (priceCents ?? 0),
    softwareChanges: presentation.software.changes,
    trialDays: inputs.trialDays,
    code: presentation.code,
    isDefaultDeal: presentation.isDefaultDeal,
  };
}
